
/**
 * area-selector.js
 *
 * Modal UI + wiring ONLY. All area data, localStorage access, validation,
 * and warehouse resolution live in warehouse-area.js (Step 1) and are never
 * duplicated here — this file only calls into that module's public API:
 *
 *   WarehouseArea.getAvailableAreas()
 *   WarehouseArea.getSelectedArea()
 *   WarehouseArea.setSelectedArea(areaKey)
 *   WarehouseArea.getResolvedArea()
 *   WarehouseArea.resolveWarehouse(areaKey)
 *   WarehouseArea.NO_AREA_SELECTED
 *
 * Load order requirement: this file must load AFTER warehouse-area.js.
 *
 * ── Selection UI ─────────────────────────────────────────────────────────
 * Areas are rendered as <option> elements inside a native <select>
 * (data-area-selector-select), with a separate Confirm button
 * (data-area-selector-confirm) that commits the choice. The Confirm button
 * is always clickable — it is not gated on the selection actually changing.
 *
 * ── Selection flow ───────────────────────────────────────────────────────
 * On confirming an area (header-triggered OR gated flow):
 *   1. WarehouseArea.setSelectedArea(areaKey)
 *   2. Fire a `warehouse-area:selected` CustomEvent on `document` with
 *      { areaKey, area } in `detail` — hook point for future steps
 *      (cart conflict handling) without modifying this file.
 *   3. Close the modal
 *   4. Full page reload — always, in both flows. This matches the industry
 *      standard (Samsung, Apple, ASOS) and ensures the page re-renders
 *      with accurate area-specific data (stock, availability, etc.)
 *      without requiring every component to live-update itself.
 *      The `warehouse-area:selected` event is still cancelable, so a future
 *      cart-conflict step can call event.preventDefault() to intercept
 *      before the reload if needed.
 *
 * ── First-visit / stale-selection auto-open ──────────────────────────────
 * The modal auto-opens once per browser session when getResolvedArea()
 * returns NO_AREA_SELECTED on page load. This covers:
 *   - Genuine first-ever visit (nothing in localStorage)
 *   - Stale/invalid selection (area was disabled or renamed in the
 *     metaobject since the customer last chose — getResolvedArea() already
 *     returns NO_AREA_SELECTED in this case, no extra logic needed here)
 *
 * "Once per session" is enforced via sessionStorage key
 * `warehouse_area_dismissed`. If the customer closes the modal without
 * choosing (X button, Escape, overlay click), the dismissed key is set and
 * the modal does not auto-open again for the rest of that browser session.
 * It WILL fire again on their next session (new tab/window after closing
 * the browser), and WILL fire again mid-session if their stored selection
 * becomes stale (because that clears NO_AREA_SELECTED → fresh trigger
 * even if dismissed this session — see maybeAutoOpen()).
 *
 * Customers can always open the modal manually via the header trigger
 * regardless of dismissed state.
 *
 * ── ensureAreaSelected() ─────────────────────────────────────────────────
 * Reusable gate for Step 4 (PDP gating) and beyond. If an area is already
 * resolved, does nothing. If not, opens the modal. Either way, the
 * customer ends up selecting an area and the page reloads — the gated
 * action (Add to Cart, Buy Now) is abandoned and the customer re-attempts
 * it on the reloaded page, which now shows accurate area-specific data.
 * This matches the Samsung/Apple pattern and is simpler than trying to
 * resume the original action after selection.
 */

(function () {
  "use strict";

  var DISMISSED_KEY = "warehouse_area_dismissed";

  if (typeof window.WarehouseArea === "undefined") {
    console.error(
      "[area-selector] WarehouseArea module not found. Make sure warehouse-area.js loads before area-selector.js."
    );
    return;
  }

  var WA = window.WarehouseArea;

  var modalEl = null;
  var selectEl = null;
  var confirmBtnEl = null;
  var fieldEl = null;
  var emptyStateEl = null;
  var indicatorValueEl = null;
  var lastFocusedElement = null;

  function init() {
    modalEl = document.querySelector("[data-area-selector-modal]");
    selectEl = document.querySelector("[data-area-selector-select]");
    confirmBtnEl = document.querySelector("[data-area-selector-confirm]");
    fieldEl = document.querySelector("[data-area-selector-field]");
    emptyStateEl = document.querySelector("[data-area-selector-empty]");

    var indicatorTrigger = document.querySelector("[data-area-selector-trigger]");
    indicatorValueEl = indicatorTrigger
      ? indicatorTrigger.querySelector(".header__deliver-to-value")
      : null;

    if (!indicatorValueEl) {
      console.warn(
        "[area-selector] Could not find .header__deliver-to-value inside the trigger element. Header indicator text will not update."
      );
    }

    if (!modalEl || !selectEl || !confirmBtnEl) {
      console.error(
        "[area-selector] Required modal elements not found in the DOM. Is sections/area-selector-modal.liquid rendered?"
      );
      return;
    }

    // Header trigger + any other [data-area-selector-trigger] elements
    var triggers = document.querySelectorAll("[data-area-selector-trigger]");
    triggers.forEach(function (trigger) {
      trigger.addEventListener("click", function (event) {
        event.preventDefault();
        openAreaModal();
      });
    });

    // Close interactions — all treated identically (X, overlay, Escape)
    var closers = document.querySelectorAll("[data-area-selector-close]");
    closers.forEach(function (closer) {
      closer.addEventListener("click", function () {
        dismissAndClose();
      });
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && isModalOpen()) {
        dismissAndClose();
      }
    });

    // Confirm button commits whatever is currently chosen in the <select>
    confirmBtnEl.addEventListener("click", function () {
      var areaKey = selectEl.value;
      if (!areaKey) return;

      var areas = WA.getAvailableAreas() || [];
      var area = null;
      for (var i = 0; i < areas.length; i++) {
        if (areas[i].area_key === areaKey) {
          area = areas[i];
          break;
        }
      }
      if (!area) return;

      selectArea(areaKey, area);
    });

    updateIndicator();
    maybeAutoOpen();
  }

  /**
   * Auto-opens the modal if:
   *   1. No area is currently resolved (first visit, or stale selection), AND
   *   2. The customer has not already dismissed it this browser session
   *
   * Stale-selection case is handled automatically — getResolvedArea()
   * already returns NO_AREA_SELECTED when the stored key no longer matches
   * any active area, so no extra staleness check is needed here.
   *
   * The dismissed flag is intentionally checked against the CURRENT resolved
   * state, not stored at dismiss-time — this means if a customer dismisses
   * the modal, then their previously valid selection becomes stale later in
   * the same session (unlikely but possible), the modal will re-fire because
   * NO_AREA_SELECTED overrides the dismissed flag.
   */
  function maybeAutoOpen() {
    if (WA.getResolvedArea() !== WA.NO_AREA_SELECTED) return;

    // If they have a stale selection (something was stored but is no longer
    // valid), always re-trigger regardless of dismissed state — their context
    // has genuinely changed and they need to re-select.
    var hasStaleSelection = !!WA.getSelectedArea() &&
      WA.getResolvedArea() === WA.NO_AREA_SELECTED;

    if (hasStaleSelection) {
      openAreaModal();
      return;
    }

    // Pure first-visit (nothing ever stored): respect the dismissed flag
    try {
      if (sessionStorage.getItem(DISMISSED_KEY)) return;
    } catch (e) {
      // sessionStorage unavailable (private mode etc.) — open anyway
    }

    openAreaModal();
  }

  /**
   * Closes the modal and records that the customer dismissed it this
   * session, so maybeAutoOpen() doesn't re-fire on subsequent page loads
   * within the same browser session.
   *
   * Called by X button, overlay click, and Escape key — all three are
   * treated identically.
   */
  function dismissAndClose() {
    try {
      sessionStorage.setItem(DISMISSED_KEY, "1");
    } catch (e) {
      // sessionStorage unavailable — dismiss silently
    }
    closeAreaModal();
  }

  function isModalOpen() {
    return modalEl && modalEl.classList.contains("is-open");
  }

  /**
   * Opens the modal and (re)renders the <select> options from the current
   * data layer state. Exposed on window so later steps can reopen it
   * programmatically (e.g. ensureAreaSelected(), cart conflict handling).
   */
  function openAreaModal() {
    if (!modalEl) return;

    renderOptions();

    lastFocusedElement = document.activeElement;
    modalEl.classList.add("is-open");
    modalEl.setAttribute("aria-hidden", "false");
    document.body.classList.add("area-selector-modal-open");

    var focusTarget = fieldEl && !fieldEl.hidden ? selectEl : modalEl;
    focusTarget.focus();
  }

  function closeAreaModal() {
    if (!modalEl) return;

    modalEl.classList.remove("is-open");
    modalEl.setAttribute("aria-hidden", "true");
    document.body.classList.remove("area-selector-modal-open");

    if (lastFocusedElement && typeof lastFocusedElement.focus === "function") {
      lastFocusedElement.focus();
    }
  }

  /**
   * Rebuilds the <select>'s <option> list from getAvailableAreas(), with
   * the currently resolved area pre-selected. Never hardcodes area
   * names/keys.
   */
  function renderOptions() {
    var areas = WA.getAvailableAreas() || [];
    var resolved = WA.getResolvedArea();
    var currentKey =
      resolved && resolved !== WA.NO_AREA_SELECTED ? resolved.area_key : null;

    selectEl.innerHTML = "";

    if (areas.length === 0) {
      if (fieldEl) fieldEl.hidden = true;
      if (emptyStateEl) emptyStateEl.hidden = false;
      return;
    }

    if (fieldEl) fieldEl.hidden = false;
    if (emptyStateEl) emptyStateEl.hidden = true;

    areas.forEach(function (area) {
      var option = document.createElement("option");
      option.value = area.area_key;
      option.textContent = area.area_name;
      if (area.area_key === currentKey) {
        option.selected = true;
      }
      selectEl.appendChild(option);
    });
  }

  /**
   * Fires the documented hook event. Cancelable so a future cart-conflict
   * step can call event.preventDefault() to intercept before the reload.
   */
  function dispatchAreaSelected(areaKey, area) {
    var event = new CustomEvent("warehouse-area:selected", {
      detail: { areaKey: areaKey, area: area },
      cancelable: true
    });
    document.dispatchEvent(event);
    return event;
  }

  /**
   * The full selection flow — identical for both header-triggered and gated
   * flows. Always reloads after selection (matches Samsung/Apple/ASOS
   * standard; ensures the page re-renders with accurate area-specific data).
   */
  function selectArea(areaKey, area) {
    WA.setSelectedArea(areaKey);

    // Clear dismissed flag on confirmed selection so the next session
    // starts fresh (in case they ever clear their localStorage and end up
    // with no area again — we want the auto-open to fire).
    try {
      sessionStorage.removeItem(DISMISSED_KEY);
    } catch (e) {}

    var event = dispatchAreaSelected(areaKey, area);

    closeAreaModal();

    if (!event.defaultPrevented) {
      window.location.reload();
    }
  }

  /**
   * Reusable gate for Step 4 (PDP gating) and beyond.
   *
   * If an area is already resolved: does nothing (the Add to Cart / Buy Now
   * action proceeds normally).
   *
   * If no area is resolved: opens the modal. The customer selects an area,
   * the page reloads. The original gated action (Add to Cart click, etc.)
   * is abandoned — the customer re-attempts it on the reloaded page, which
   * now shows accurate area-specific availability. This matches the
   * Samsung/Apple pattern and avoids complexity of resuming in-progress
   * actions after an async modal flow.
   *
   * Returns true if an area was already resolved (caller can proceed),
   * false if the modal was opened (caller should abort its action).
   *
   * Usage from Step 4:
   *   if (!window.ensureAreaSelected()) {
   *     event.preventDefault(); // abort the Add to Cart / Buy Now
   *     return;
   *   }
   *   // proceed with add-to-cart logic
   */
  function ensureAreaSelected() {
    if (WA.getResolvedArea() !== WA.NO_AREA_SELECTED) {
      return true;
    }
    openAreaModal();
    return false;
  }

  /**
   * Updates the header trigger's "Select location" text in place.
   * Called once on page load; the full reload after selection keeps it
   * in sync on subsequent pages — no extra sync logic needed.
   */
  function updateIndicator() {
    if (!indicatorValueEl) return;

    var resolved = WA.getResolvedArea();

    if (resolved === WA.NO_AREA_SELECTED) {
      indicatorValueEl.classList.remove("header__deliver-to-value--selected");
      return;
    }

    indicatorValueEl.classList.add("header__deliver-to-value--selected");
    indicatorValueEl.textContent = resolved.area_name;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Exposed globally for later steps and debugging
  window.openAreaModal = openAreaModal;
  window.closeAreaModal = closeAreaModal;
  window.ensureAreaSelected = ensureAreaSelected;
})();
